// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { Box, useTheme } from '@mui/system';
import React, { useMemo, useRef, useState } from 'react';
import { Button, CircularProgress, Typography } from '@mui/material';
import { PROJECTS_ROUTE } from 'Utils/routes';
import { InvalidateMapCoordinatesAction, SetMapFolderAction, SetShowMapviewAction } from 'Features/projectList/projectListActions';
import { SetProjectViewModeThunk } from 'Features/projectList/projectListThunk';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'Hooks';
import ProjectListItem from 'Features/projectList/ProjectListItem';
import ProjectListRow from 'Features/projectList/ProjectListRow';
import ProjectsControls from 'Features/projectList/ProjectsSubheader/ProjectsControls';
import ProjectsMapToggle from 'Features/projectList/ProjectsMapToggle';
import { FOLDER_ORDER_SORT_FILTER_OPTIONS } from 'Features/projectList/projectViewOptions';
import { narrowProjects } from 'Features/projectList/filterProjects';
import { describeProjectsNotFound } from 'Features/projectList/projectsNotFound';
import { useRevealSelectedProject } from 'Features/projectList/useRevealSelectedProject';
import SearchField from 'Components/SearchField';
import FolderBreadcrumb from 'Components/FolderBreadcrumb';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { IAvailableProject, IAvailableProjectsResponse } from 'Utils/typings/state/folder';
import stylesDef from './styles';
import FolderName from '../FolderName';
import { selectProjectsInFolder } from '../folderListSelector';
import { getAvailableProjectsThunk } from '../folderListThunk';
import MoveProjectBulkDialog from './MoveProjectBulkDialog';
import { FolderUnavailable } from './FolderUnavailable';
import { useFolderPage } from './useFolderPage';
import { SetProjectsInFolderAction } from '../folderListActions';

interface IFolderPageProps {
  folderId: string | undefined;
}

const FolderPage = ({ folderId }: IFolderPageProps) => {
  // Constants
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Redux variables
  const projects = useAppSelector(
    selectProjectsInFolder,
  );

  const {
    folder: folderDetails, folderNotFound, folderUnreadable, projectsLoading, projectsFailed, reloadFolders, reloadProjects,
  } = useFolderPage(folderId);
  const [showMoveProjectBulkDialog, setShowMoveProjectBulkDialog] = useState<boolean>(false);
  const [availableProjects, setAvailableProjects] = useState<IAvailableProject[]>([]);
  const [loadingAvailableProjects, setLoadingAvailableProjects] = useState<boolean>(false);
  // A folder's projects arrive whole, so searching, filtering and sorting happen here rather
  // than through the paginated dashboard's store - which this page must not disturb.
  const [keyword, setKeyword] = useState<string>('');
  const [sortFilter, setSortFilter] = useState<string>('updatedAtD');
  const [orderFilter, setOrderFilter] = useState<ProjectOrderSortFilter>('all');
  // The list/tile choice is the one exception, shared with the dashboard: a folder page opens
  // the way the dashboard was left, and a switch here carries back to it (AUT-6509).
  const viewMode = useAppSelector((state) => state.projectList.projectViewMode);

  // There is one map view, on the dashboard. Opening it from here hands it this folder, so it
  // arrives with the folder selected in its rail and only the folder's projects plotted -
  // rather than this page growing a second map of its own.
  const openFolderOnMap = () => {
    if (folderId) dispatch(SetMapFolderAction(folderId));
    dispatch(SetShowMapviewAction(true));
    navigate(PROJECTS_ROUTE);
  };

  const visibleProjects = useMemo(
    () => narrowProjects(projects, { keyword, orderFilter, sortFilter }),
    [projects, keyword, orderFilter, sortFilter],
  );

  // Leaving the map inside this folder lands here on the project whose pin was picked: its
  // row is scrolled into view and outlined, as the dashboard does for an unfiled project. The
  // hook is handed what is on screen - nothing while the fetch's loading state hides the list.
  const mapPickToReveal = useAppSelector((state) => state.projectList.mapPickToReveal);
  const listRef = useRef<HTMLDivElement>(null);
  const renderedProjects = useMemo(() => (projectsLoading ? [] : visibleProjects), [projectsLoading, visibleProjects]);
  useRevealSelectedProject({ listRef, projects: renderedProjects, sortKey: sortFilter });
  // An empty folder gets the onboarding card; a search or filter that matched nothing in a
  // folder that has projects gets the dashboard's not-found message instead.
  const projectsInHand = !projectsLoading && !projectsFailed;
  const folderIsEmpty = projectsInHand && projects.length === 0;
  const nothingMatched = projectsInHand && projects.length > 0 && visibleProjects.length === 0;
  const notFoundMessage = describeProjectsNotFound(orderFilter, keyword.trim());

  // Methods
  const handleMoveProjectToFolderSuccess = (project?: IProject) => {
    // Update the projects list with the new project
    const updatedProjects = projects.filter((p) => p._id !== project?._id);
    dispatch(SetProjectsInFolderAction(updatedProjects));
    // The dashboard's cached set still has this project in this folder; it refetches next time.
    dispatch(InvalidateMapCoordinatesAction());
  };

  const handleMoveProjectBulkOpen = async () => {
    setLoadingAvailableProjects(true);
    setShowMoveProjectBulkDialog(true);
    try {
      const availableProjectsResponse: IAvailableProjectsResponse = await dispatch(getAvailableProjectsThunk(folderId as string));
      setAvailableProjects(availableProjectsResponse.projects);
    } catch (error) {
      // The dialog stays open with nothing to pick from rather than on a spinner that never ends.
      console.error('Loading the projects available to add to the folder failed', error);
    } finally {
      setLoadingAvailableProjects(false);
    }
  };

  // HTML
  if (folderNotFound) {
    return (
      <Box sx={styles.mapviewLayoutLeft}>
        <FolderUnavailable reason="notFound" onTryAgain={undefined} />
      </Box>
    );
  }

  if (folderUnreadable) {
    return (
      <Box sx={styles.mapviewLayoutLeft}>
        <FolderUnavailable
          reason="unreadable"
          onTryAgain={() => {
            reloadFolders();
            reloadProjects();
          }}
        />
      </Box>
    );
  }

  // Opened from its link, the folder is known once the organization's folders are read
  if (!folderDetails) {
    return (
      <Box sx={styles.mapviewLayoutLeft}>
        <Box sx={styles.circularProgressContainer}>
          <CircularProgress size={50} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={styles.mapviewLayoutLeft}>
      {/* Breadcrumb */}
      <Box sx={styles.breadcrumbsContainer}>
        {/* No click handler: on the folder page All Projects is a link to the dashboard. */}
        <FolderBreadcrumb onAllProjectsClick={undefined}>
          <FolderName initialFolderName={folderDetails.name} folderId={folderDetails._id} />
        </FolderBreadcrumb>
        {
          projects.length > 0 && (
            <Button variant="contained" color="primary" sx={styles.addProjectsButton} onClick={handleMoveProjectBulkOpen}>
              Add Projects
            </Button>
          )
        }
      </Box>
      <Box sx={styles.searchRow}>
        <SearchField
          placeholder="Search by projects or folders..."
          value={keyword}
          sx={styles.searchField}
          onChange={(value: string) => setKeyword(value)}
        />
        {/* There is one map view, on the dashboard. The switch's Map segment hands it the folder:
            the dashboard opens with the folder selected in its rail and only its projects on
            the map. */}
        <ProjectsMapToggle
          value="projects"
          onChange={(view) => {
            if (view === 'map') openFolderOnMap();
          }}
        />
      </Box>
      {/* The same controls the dashboard shows, minus the Unassigned filter: inside a
          folder every project is assigned, so those two would select everything or nothing. */}
      <ProjectsControls
        title={folderDetails.name}
        sortFilter={sortFilter}
        onSortChange={setSortFilter}
        orderFilter={orderFilter}
        onOrderFilterChange={setOrderFilter}
        filterOptions={FOLDER_ORDER_SORT_FILTER_OPTIONS}
        onOrderFilterOpen={undefined}
        viewMode={viewMode}
        onViewModeChange={(mode) => dispatch(SetProjectViewModeThunk(mode))}
      />
      <Box sx={[projectsLoading && styles.projectsContainer]}>
        {/* Loading state */}
        {projectsLoading && (
          <Box sx={styles.circularProgressContainer}>
            <CircularProgress
              size={50}
            />
          </Box>
        )}

        {/* Render projects */}
        {projectsInHand && visibleProjects.length > 0 && (
          <Box sx={viewMode === 'cards' ? styles.gridList : styles.rowList} ref={listRef}>
            {visibleProjects.map((project: IProject) => (viewMode === 'cards' ? (
              <ProjectListItem
                project={project}
                key={project._id}
                isInsideFolder
                handleMoveProjectToFolderSuccess={handleMoveProjectToFolderSuccess}
                isSelected={project._id === mapPickToReveal}
              />
            ) : (
              <ProjectListRow
                project={project}
                key={project._id}
                isInsideFolder
                handleMoveProjectToFolderSuccess={handleMoveProjectToFolderSuccess}
                isSelected={project._id === mapPickToReveal}
              />
            )))}
          </Box>
        )}

        {projectsFailed && (
          <Box sx={styles.loadFailed}>
            <Typography variant="h3">This folder&apos;s projects could not be loaded.</Typography>
            <Button variant="contained" color="primary" onClick={reloadProjects}>
              Try again
            </Button>
          </Box>
        )}

        {nothingMatched && (
          <Box sx={styles.notFound}>
            <Typography variant="h1">{notFoundMessage}</Typography>
          </Box>
        )}

        {/* No projects in the folder yet */}
        {folderIsEmpty && (
          <Box sx={styles.noProjectsContainer}>
            <PlaceOutlinedIcon sx={styles.noProjectsIcon} />
            <Box sx={styles.noProjectsTextContainer}>
              <Typography variant="h3">
                There are no projects to show.
              </Typography>
              <Typography variant="body1">
                Click on the button below to start adding projects.
              </Typography>
            </Box>
            <Button variant="contained" color="primary" onClick={handleMoveProjectBulkOpen}>
              Add Projects
            </Button>
          </Box>
        )}
      </Box>
      <MoveProjectBulkDialog
        open={showMoveProjectBulkDialog}
        onClose={() => {
          setShowMoveProjectBulkDialog(false);
          reloadProjects();
        }}
        availableProjects={availableProjects}
        loadingAvailableProjects={loadingAvailableProjects}
      />
    </Box>
  );
};

// One page per folder: a change of folder mounts a fresh page, so nothing of the folder left behind (its projects, a
// read still in flight, a search typed there) carries over to the next one.
const FolderDetails = () => {
  const { folderId } = useParams();
  return <FolderPage key={folderId} folderId={folderId} />;
};

export default FolderDetails;
